import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject, InjectionToken } from '@angular/core';
import { Router } from '@angular/router';
import { OurAuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { catchError, Observable, switchMap, throwError } from 'rxjs';

/**
 * Where to send a visitor whose session could not be refreshed, given the URL they were on. The dashboard
 * keeps its own sign-in except under its embedded `/messenger` area; the standalone messenger app provides a
 * resolver that always returns its own root-level sign-in.
 */
export const SIGN_IN_URL_RESOLVER = new InjectionToken<(currentUrl: string | undefined) => string>('SIGN_IN_URL_RESOLVER', {
    providedIn: 'root',
    factory: () => currentUrl => (currentUrl?.startsWith('/messenger') ? '/messenger/sign-in' : '/sign-in'),
});

export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const authService = inject(OurAuthService);
    const router = inject(Router);
    const signInUrlFor = inject(SIGN_IN_URL_RESOLVER);

    if (req.headers.has('skip-interceptor')) {
        const newReq = req.clone({
            headers: req.headers.delete('skip-interceptor'),
        });
        return next(newReq);
    }

    let newReq = req.clone({
        headers: req.headers
            .append('x-lang', localStorage.getItem('language') || 'en')
            .append('x-deviceId', 'www'),
    });

    if (authService.accessToken && !AuthUtils.isTokenExpired(authService.accessToken)) {
        newReq = newReq.clone({
            headers: newReq.headers.append('Authorization', 'Bearer ' + authService.accessToken),
        });
    }

    return next(newReq).pipe(
        catchError((error) => {
            if (error instanceof HttpErrorResponse && error.status === 401) {
                return authService.getUserByRefreshToken(localStorage.getItem('refreshToken')).pipe(
                    switchMap(() => {
                        const retryReq = newReq.clone({
                            headers: newReq.headers.set(
                                'Authorization',
                                'Bearer ' + authService.accessToken
                            ),
                        });
                        return next(retryReq);
                    }),
                    catchError(() => {
                        authService.signOut().subscribe();
                        // The messenger app has its own sign-in; do not bounce its users into the dashboard's.
                        router.navigate([signInUrlFor(router.url)]);
                        return throwError(() => error);
                    })
                );
            }
            return throwError(() => error);
        })
    );
};
